const tokenKey = "_tokenresponse_";
// The auth domain, kept beside the tokens. /callback also puts it in a cookie, but that
// cookie has no expiry and so dies with the browser session, while the refresh token is
// good for 30 days — a refresh must not depend on a cookie outliving the login.
const authDomainKey = "_cognitoauthdomain_";
const redirecturl = () => window.location.origin + "/callback";

function JSON_to_URLEncoded(element, key, list) {
	const out = list || [];
	if (typeof element == "object") {
		for (const idx in element)
			JSON_to_URLEncoded(element[idx], key ? key + "[" + idx + "]" : idx, out);
	} else {
		out.push(key + "=" + encodeURIComponent(element));
	}
	return out.join("&");
}

function getCookie(name) {
	const r = document.cookie.match("\\b" + name + "=([^;]*)\\b");
	return r ? r[1] : null;
}

export async function HandleCallback() {
	console.log("perform auth with query", window.location.search);

	const searchParams = new URLSearchParams(window.location.search);
	const code = searchParams.get("code");

	const clientid = getCookie("COGNITO_CLIENT_ID");
	const cognitoauthdomain = getCookie("COGNITO_AUTH_DOMAIN");

	const payload = {
		grant_type: "authorization_code",
		client_id: clientid,
		redirect_uri: redirecturl(),
		code: code,
	};

	const urlencoded = JSON_to_URLEncoded(payload);

	const tokenendpoint = cognitoauthdomain + `/oauth2/token`;

	const resp = await fetch(tokenendpoint, {
		method: "POST",
		headers: {
			"Content-Type": "application/x-www-form-urlencoded",
		},
		body: urlencoded,
	});

	const json = await resp.json();
	console.log(json);

	if ("error" in json) {
		console.error(json["error"]);
		window.localStorage.removeItem(tokenKey);
		window.location.href = "/";
		return;
	}

	window.localStorage.setItem(tokenKey, JSON.stringify(json));
	if (cognitoauthdomain) window.localStorage.setItem(authDomainKey, cognitoauthdomain);

	const event = new Event("login");
	window.dispatchEvent(event);

	window.history.replaceState({}, "", "/");
}

let shareInFlight = false;

/**
 * Sign in from a share link: /share#u=<user>&p=<password>.
 *
 * A share link is an ordinary Cognito user — view-only because it is in no full-access
 * group, scoped to the clusters whose groups it is in, and revoked by disabling it. The
 * Hosted UI has no way to take credentials from a URL, so the page does the one call
 * the Hosted UI would have done. The credentials ride in the fragment, which the browser
 * never sends to the server.
 */
export async function HandleShare() {
	const params = new URLSearchParams(window.location.hash.slice(1));
	const username = params.get("u");
	const password = params.get("p");

	// Stripping the fragment below goes through replaceState, which re-runs the router and
	// lands back here with no credentials; that call must leave the first one alone.
	if (shareInFlight) return;
	if (!username || !password) {
		window.location.href = "/";
		return;
	}
	shareInFlight = true;
	// Out of the address bar before anything slow happens, so it is not left on screen
	// or picked up by a bookmark.
	window.history.replaceState({}, "", "/share");

	const region = getCookie("COGNITO_REGION_ID");
	const clientid = getCookie("COGNITO_CLIENT_ID");
	const cognitoauthdomain = getCookie("COGNITO_AUTH_DOMAIN");

	let result = null;
	try {
		const resp = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-amz-json-1.1",
				"X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
			},
			body: JSON.stringify({
				AuthFlow: "USER_PASSWORD_AUTH",
				ClientId: clientid,
				AuthParameters: {USERNAME: username, PASSWORD: password},
			}),
		});
		// A disabled or deleted user, a wrong password, or a challenge such as a forced
		// password change all come back without an AuthenticationResult.
		result = (await resp.json()).AuthenticationResult;
	} catch (err) {
		console.error("share sign-in failed", err);
	}

	shareInFlight = false;

	if (!result || !result.AccessToken) {
		console.error("share link was refused");
		window.localStorage.removeItem(tokenKey);
		window.location.href = "/login";
		return;
	}

	// The same shape /oauth2/token returns, so everything downstream — the API calls, the
	// stream, view-only, renewal — cannot tell how the session started.
	window.localStorage.setItem(
		tokenKey,
		JSON.stringify({
			access_token: result.AccessToken,
			id_token: result.IdToken,
			refresh_token: result.RefreshToken,
			expires_in: result.ExpiresIn,
			token_type: result.TokenType,
		}),
	);
	if (cognitoauthdomain) window.localStorage.setItem(authDomainKey, cognitoauthdomain);

	window.dispatchEvent(new Event("login"));
	window.history.replaceState({}, "", "/");
}

export function getAuthUser() {
	const tokenresponse = window.localStorage.getItem(tokenKey);
	return tokenresponse;
}

function parsedResp() {
	const tokenresp = getAuthUser();
	if (IsValid(tokenresp) === false) {
		return {};
	}

	return JSON.parse(tokenresp);
}

// JWT payloads are base64URL, whose - and _ atob() rejects outright, and atob() also
// yields bytes rather than text so a non-ASCII character in a name comes out mangled.
// ASCII payloads happen to avoid + and / in practice — only the last sextet of each
// group can reach them, which needs a >, ~ or ? byte — which is why this has worked.
function decodeJwtPayload(jwt) {
	const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
	const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
	return JSON.parse(new TextDecoder().decode(bytes));
}

function parsedIDToken() {
	return decodeJwtPayload(parsedResp().id_token);
}

function parsedAccessToken() {
	return decodeJwtPayload(parsedResp().access_token);
}

// Treat a token as expired slightly early, so one that runs out mid-request is caught
// here rather than as a puzzling rejection from the server.
const EXPIRY_SKEW_SECONDS = 30;

// The server validates exp now. It used to accept any token it had ever signed, so
// nothing on this side had to think about expiry; a stored token that has run out now
// fails every request instead.
function expiresWithin(seconds) {
	try {
		const exp = parsedAccessToken().exp;
		// No exp claim, or an unreadable token: not ours to judge, let the server say.
		if (!exp) return false;
		return exp - seconds <= Date.now() / 1000;
	} catch {
		return false;
	}
}

export function TokenExpired() {
	return expiresWithin(EXPIRY_SKEW_SECONDS);
}

// How far ahead of expiry the access token is renewed.
//
// Cognito issues it for an hour while a viewer holds a stream open for far longer, so
// without renewal every session is thrown back to the login page mid-stream — for a drone
// operator, mid-flight. The refresh token that makes this possible lasts 30 days and has
// been stored since the first login; nothing ever used it.
//
// Five minutes because the only thing driving this is the 5s app-list poll, and the margin
// has to cover a laptop that was asleep, a poll that failed, and a slow token endpoint.
const REFRESH_MARGIN_SECONDS = 300;

function authDomain() {
	return getCookie("COGNITO_AUTH_DOMAIN") || window.localStorage.getItem(authDomainKey);
}

async function requestRefresh() {
	const stored = parsedResp();
	const domain = authDomain();
	let clientId = null;
	try {
		// From the token itself rather than the cookie: it is the client that actually
		// issued this session, and it cannot go missing while the token is still here.
		clientId = parsedAccessToken().client_id;
	} catch {
		// Unreadable token; there is nothing to renew.
	}
	if (!stored.refresh_token || !domain || !clientId) return false;

	try {
		const resp = await fetch(domain + "/oauth2/token", {
			method: "POST",
			headers: {"Content-Type": "application/x-www-form-urlencoded"},
			body: JSON_to_URLEncoded({
				grant_type: "refresh_token",
				client_id: clientId,
				refresh_token: stored.refresh_token,
			}),
		});
		if (!resp.ok) return false;
		const json = await resp.json();
		if ("error" in json || !json.access_token) return false;
		// Merged, not replaced: the response carries no refresh_token, so the stored one
		// has to survive or the next renewal has nothing to present.
		window.localStorage.setItem(tokenKey, JSON.stringify({...stored, ...json}));
		return true;
	} catch {
		// Offline, or the token endpoint is unreachable. The existing token is still
		// valid for now, so this is worth retrying rather than logging out over.
		return false;
	}
}

let refreshInFlight = null;

/**
 * Renew the access token when it is close to running out.
 *
 * Called from the app-list poll, so it runs every few seconds for the whole session and
 * needs to be cheap and idempotent: it returns immediately unless expiry is near, and
 * concurrent callers share one request.
 * @returns {Promise<boolean>} false only when renewal was needed and failed
 */
export async function EnsureFreshToken() {
	if (!expiresWithin(REFRESH_MARGIN_SECONDS)) return true;
	if (!refreshInFlight) {
		refreshInFlight = requestRefresh().finally(() => {
			refreshInFlight = null;
		});
	}
	return refreshInFlight;
}

let reauthenticating = false;

/**
 * Send the user back through login, at most once per page load.
 *
 * The cap matters: if the server is rejecting us for something logging in again
 * cannot fix (wrong client id, a group that was revoked), an unguarded redirect would
 * bounce between / and /login forever and hide the real error.
 * @returns {boolean} true if a redirect was started
 */
export function Reauthenticate() {
	if (reauthenticating) return false;
	reauthenticating = true;
	console.warn("the session is no longer valid, signing in again");
	window.localStorage.removeItem(tokenKey);
	window.location.href = "/login";
	return true;
}

export function Email() {
	try {
		const parsed = parsedIDToken();

		// A share-link user has no email, only a username.
		return parsed.email || parsed["cognito:username"] || "";
	} catch {}

	return "";
}

export function IsReadOnly() {
	try {
		const parsed = parsedAccessToken();

		return !(
			parsed["cognito:groups"].includes("Bolt6") ||
			parsed["cognito:groups"].includes("WebRemoteFullAccess")
		);
	} catch {}

	return true;
}

// The token's subject: an opaque per-user id, stable across an email change and
// meaningless to anyone reading it out of storage.
export function Subject() {
	try {
		return parsedAccessToken().sub || "";
	} catch {
		return "";
	}
}

export function Username() {
	const parsed = parsedAccessToken();

	return parsed.name || parsed.username;
}

function IsValid(token) {
	if (token == null) return false;
	return true;
}

export function AuthToken() {
	const auths = parsedResp();
	if (auths == null) {
		return "";
	}
	return parsedResp().access_token;
}

export async function EnsureLogin() {
	const token = getAuthUser();
	if (IsValid(token) === false) {
		window.location.href = "/login";
		return false;
	}
	// A tab left open overnight holds a token the server rejects on every call.
	// Without this the UI loads fine, the app list quietly stops updating, and
	// connecting reports that the server refused the connection.
	//
	// Renewing comes first: only a refresh token that is itself gone, expired or revoked
	// justifies sending the user back through login.
	if (TokenExpired() && !(await EnsureFreshToken())) {
		Reauthenticate();
		return false;
	}

	return true;
}

export function Logout() {
	// Read before discarding the token: the client id is a claim inside it.
	const domain = authDomain();
	let clientId = null;
	try {
		clientId = parsedAccessToken().client_id;
	} catch {
		// Unreadable token; there is no provider session to end.
	}

	window.localStorage.removeItem(tokenKey);

	if (!domain || !clientId) {
		// Nothing to sign out of — the dev login writes a token without ever visiting
		// Cognito, so there is no provider session behind it.
		window.location.href = "/";
		return;
	}

	// End the provider's session too, not just ours. Cognito keeps its own session
	// cookie, so dropping the token alone signs nobody out: the next visit to /login is
	// answered from that cookie and lands straight back in as the same user, with no
	// prompt. On a shared machine that hands the account to whoever sits down next, and
	// it is invisible, because the app looks signed out.
	//
	// logout_uri has to be registered in the app client's LogoutURLs or Cognito answers
	// with an error page instead of signing out.
	const logoutUri = encodeURIComponent(`${window.location.origin}/`);
	window.location.href = `${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
}
